import { AttributeValueSelectV2 } from '@frontend/attribute/select';
import { AttributeValue } from '@frontend/attribute/types';
import { EntityType } from '@frontend/common';
import { MultiObjectSelectInput } from '@frontend/components/basic-forms';
import { CommonMessage, useLocale } from '@frontend/lang';
import { defaultMapWithAttributes } from '@frontend/product/badge';
import { IProduct } from '@frontend/product/types';
import { FormattedMessage } from 'react-intl';

import UpdateModal from '../../../../modal/update-modal/update-modal.component';
import { useLinkProductsModal } from './link-products-modal.controller';

export interface LinkProductsModalProps {
    attributeId: string;
    successCallback?: () => void;
}

export const LinkProductsModal = (props: LinkProductsModalProps) => {
    const viewProps = useLinkProductsModal(props);
    const { locale } = useLocale();

    return (
        <UpdateModal<{ success: boolean }, { product_ids: string[] }>
            order={[]}
            label={
                <FormattedMessage
                    id='link-products-modal.link-products-to-attribute'
                    defaultMessage='Link products to attribute'
                />
            }
            type={EntityType.PRODUCT_ATTRIBUTE}
            values={viewProps.formValues}
            onSubmit={viewProps.onSubmit}
            submitDisabled={viewProps.isSubmitDisabled}
            successMessageOverwrite={
                <FormattedMessage
                    id='attribute-detail.link-products-modal.success-message'
                    defaultMessage='Products linked to attribute successfully.'
                />
            }
            append={
                <div className='flex w-full flex-col gap-4'>
                    <MultiObjectSelectInput<IProduct, string>
                        id='link-products-modal-products'
                        label={CommonMessage.OBJECTS.PRODUCT.PLURAL}
                        onChange={(v) => viewProps.setSelectedProducts(v.map((prod) => prod.id))}
                        value={viewProps.selectedProducts}
                        fetch={viewProps.fetchAvailableProducts}
                        itemLabel={(prod: IProduct) => defaultMapWithAttributes(prod, locale)}
                        required
                    />

                    {viewProps.selectedProducts.length > 0 && (
                        <div className='border-border-default bg-background-surface flex flex-col gap-4 rounded-xl border p-4'>
                            <div className='text-foreground-default text-lg font-semibold'>
                                <FormattedMessage
                                    id='link-products-modal.assign-values'
                                    defaultMessage='Assign attribute values'
                                />
                            </div>
                            <div className='flex flex-col gap-3'>
                                {viewProps.selectedProducts.map((productId) => {
                                    const product = viewProps.productObjects.find((p) => p.id === productId);
                                    if (!product) return null;

                                    return (
                                        <div
                                            key={productId}
                                            className='flex items-center gap-3'>
                                            <div className='flex-1'>
                                                <span className='text-foreground-default text-sm font-medium'>{defaultMapWithAttributes(product, locale)}</span>
                                            </div>
                                            <div className='w-64'>
                                                <AttributeValueSelectV2
                                                    value={viewProps.productAttributeValues[productId]}
                                                    onChangeObject={(v: AttributeValue | null) => viewProps.onAttributeValueChange(productId, v?.id || null)}
                                                    queryParams={{ attribute_id: props.attributeId }}
                                                    required
                                                    isClearable
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};
